import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import HelloSign from 'hellosign-embedded';
import * as Sentry from '@sentry/browser';
import { initPostHog } from '../utils/posthog';

interface ConfigContextType {
  hellosignClientId: string | null;
  checklistEnabledDistricts: number[];
  getOrCreateHellosignClient: () => typeof HelloSign | null;
}

const ConfigContext = createContext<ConfigContextType>({
  hellosignClientId: null,
  checklistEnabledDistricts: [],
  getOrCreateHellosignClient: () => null,
});

export const useConfig = () => useContext(ConfigContext);

interface ConfigProviderProps {
  children: React.ReactNode;
}

const parseChecklistEnabledDistricts = (value: string | null | undefined): number[] => {
  if (!value) {
    return [];
  }
  return value
    .split(',')
    .map((id) => parseInt(id.trim(), 10))
    .filter((id) => !isNaN(id));
};

export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
  const [hellosignClientId, setHellosignClientId] = useState<string | null>(null);
  const [checklistEnabledDistricts, setChecklistEnabledDistricts] = useState<number[]>([]);
  const [configLoaded, setConfigLoaded] = useState(false);

  const hellosignClientRef = useRef<typeof HelloSign | null>(null);
  const clientIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (configLoaded) return;
    const fetchConfig = async () => {
      try {
        const response = await axios.get('/api/config/');

        const clientId = response.data.HELLOSIGN_CLIENT_ID;
        setHellosignClientId(clientId);
        setChecklistEnabledDistricts(
          parseChecklistEnabledDistricts(response.data.CHECKLIST_ENABLED_DISTRICTS)
        );
        initPostHog(response.data.POSTHOG_KEY || '', response.data.POSTHOG_HOST || '');
      } catch (error) {
        console.error('Failed to fetch config:', error);
      } finally {
        setConfigLoaded(true);
      }
    };

    fetchConfig();
  }, [configLoaded]);

  const getOrCreateHellosignClient = useCallback((): typeof HelloSign | null => {
    if (!hellosignClientId) {
      Sentry.captureException('HelloSign client ID not available');
      return null;
    }

    // Always close existing client to prevent event listener accumulation
    if (hellosignClientRef.current) {
      try {
        hellosignClientRef.current.close();
      } catch (error) {
        Sentry.captureException('Error closing previous HelloSign client:', error);
      }
    }

    // Always create a fresh client for each signing session
    const newClient = new HelloSign({ clientId: hellosignClientId });

    hellosignClientRef.current = newClient;
    clientIdRef.current = hellosignClientId;

    return newClient;
  }, [hellosignClientId]);

  return (
    <ConfigContext.Provider
      value={{ hellosignClientId, checklistEnabledDistricts, getOrCreateHellosignClient }}
    >
      {children}
    </ConfigContext.Provider>
  );
};
